import { Routes, Route, Navigate } from "react-router-dom"
import { WordPressAuthProvider, useWordPressAuth } from "./components/wordpress-auth-provider"
import { TimerProvider } from "./lib/timer-context"
import { ThemeProvider } from "./components/theme-provider"
import { ProductionManagementProvider } from "./lib/production-management-context"
import { SprintProvider } from "./lib/sprint-context"
import { AppSidebar } from "./components/app-sidebar"
import { SidebarProvider, SidebarInset, SidebarTrigger } from "./components/ui/sidebar"
import LoginPage from "./pages/LoginPage"
import DashboardPage from "./pages/DashboardPage"
import TimeTrackerPage from "./pages/TimeTrackerPage"
import ProjectsPage from "./pages/ProjectsPage"
import ProjectPage from "./pages/ProjectPage"
import TasksPage from "./pages/TasksPage"
import TaskPage from "./pages/TaskPage"
import InfluencerProfilePage from "./pages/InfluencerProfilePage"
import CampaignDetailsPage from "./pages/CampaignDetailsPage"
import SettingsPage from "./pages/SettingsPage"
import InvoicesPage from "./pages/InvoicesPage"
import InvoiceDetailPage from "./pages/InvoiceDetailPage"
import ProductionManagementPage from "./pages/ProductionManagementPage"
import ProductionManagementDetailPage from "./pages/ProductionManagementDetailPage"
import ProductionManagementDashboardPage from "./pages/ProductionManagementDashboardPage"
import ProductionManagementTaskPage from "./pages/ProductionManagementTaskPage"
import ProductionTimesheetsPage from "./pages/ProductionTimesheetsPage"
import { ResourcesPage } from "./pages/ResourcesPage"
import MyApplicationsPage from "./pages/MyApplicationsPage"
import ApplicationDetailPage from "./pages/ApplicationDetailPage"
import MyJobListingsPage from "./pages/MyJobListingsPage"
import JobDetailPage from "./pages/JobDetailPage"
import SupportPage from "./pages/SupportPage"
import FigmakeDashboardPage from "./pages/FigmakeDashboardPage"
import FigmakeTimesheetPage from "./pages/FigmakeTimesheetPage"

function ProtectedRoutes() {
  const { user, isLoading } = useWordPressAuth()

  // Get feature permissions from WordPress
  const features = (window as any).WP_COCKPIT?.features || {}
  const isDev = (window as any).WP_COCKPIT?.devMode || false
  
  // Debug log features
  if (isDev) {
    console.log('[App] All features:', features)
    console.log('[App] project_management feature:', features.project_management)
  }
  
  const canAccessDashboard = features.dashboard === 'yes'
  const canAccessTracker = features.tracker === 'yes'
  const canAccessProjects = features.projects === 'yes'
  const canAccessTasks = features.tasks === 'yes'
  const canAccessInfluencerProfile = features.influencer_profile === 'yes'
  const canAccessInvoices = features.invoices === 'yes'
  const canAccessProductionManagement = features.production_management === 'yes'
  const canAccessApplications = features.applications === 'yes'
  const canAccessJobs = features.jobs === 'yes'
  const canAccessSupport = features.support === 'yes'
  const canAccessSettings = features.settings === 'yes'
  
  if (isDev) console.log('[App] canAccessProductionManagement:', canAccessProductionManagement)

  // Determine default route based on permissions
  const getDefaultRoute = () => {
    // Check if only settings and support are enabled (no main features)
    const hasMainFeatures = canAccessDashboard || canAccessTracker || canAccessProjects || 
                           canAccessTasks || canAccessInvoices || canAccessProductionManagement || 
                           canAccessApplications || canAccessJobs || canAccessInfluencerProfile
    
    if (!hasMainFeatures && canAccessSettings) return '/settings'
    
    if (canAccessDashboard) return '/dashboard'
    if (canAccessTracker) return '/tracker'
    if (canAccessProjects) return '/projects'  
    if (canAccessTasks) return '/tasks'
    if (canAccessInvoices) return '/invoices'
    if (canAccessProductionManagement) return '/production-management/dashboard'
    if (canAccessApplications) return '/applications'
    if (canAccessJobs) return '/jobs'
    if (canAccessInfluencerProfile) return '/influencer-profile'
    if (canAccessSupport) return '/support'
    if (canAccessSettings) return '/settings'
    return '/dashboard' // Fallback to dashboard even if no explicit permission
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!user) {
    return <LoginPage />
  }

  // Dev debug banner to show what routes are available
  if (isDev) {
    const availableRoutes = [];
    if (canAccessDashboard) availableRoutes.push('/dashboard');
    if (canAccessTracker) availableRoutes.push('/tracker');
    if (canAccessProjects) availableRoutes.push('/projects');
    if (canAccessTasks) availableRoutes.push('/tasks');
    if (canAccessInvoices) availableRoutes.push('/invoices');
    if (canAccessProductionManagement) availableRoutes.push('/production-management');
    if (canAccessApplications) availableRoutes.push('/applications');
    if (canAccessJobs) availableRoutes.push('/jobs');
    if (canAccessInfluencerProfile) availableRoutes.push('/influencer-profile');
    if (canAccessSupport) availableRoutes.push('/support');
    if (canAccessSettings) availableRoutes.push('/settings');
    if (canAccessTracker || canAccessProductionManagement) {
      availableRoutes.push('/figmake/dashboard');
      availableRoutes.push('/figmake/timesheet');
    }
    
    console.log('[App] Current pathname:', window.location.pathname);
    console.log('[App] Available routes:', availableRoutes);
    console.log('[App] Tracker enabled:', canAccessTracker);
    console.log('[App] Production Management enabled:', canAccessProductionManagement);
  }

  return (
    <div>
      {isDev && (
        <div className="fixed top-0 right-0 z-50 bg-yellow-100 border border-yellow-400 p-2 text-xs max-w-xs">
          <div><strong>DEBUG MODE</strong></div>
          <div>Path: {window.location.pathname}</div>
          <div>Basename: {(window as any).WP_COCKPIT?.mountPath}</div>
          <div>Tracker: {canAccessTracker ? '✓' : '✗'} | ProdMgmt: {canAccessProductionManagement ? '✓' : '✗'}</div>
        </div>
      )}
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="flex h-16 items-center gap-4 px-6 border-b border-sidebar-border bg-background">
            <SidebarTrigger />
            <div className="flex-1" />
          </header>
          <main className="flex-1 overflow-auto">
          <Routes>
            {canAccessDashboard && <Route path="/dashboard" element={<DashboardPage />} />}
            {canAccessTracker && <Route path="/tracker" element={<Navigate to="/tracker/timer" replace />} />}
            {canAccessTracker && <Route path="/tracker/:tab" element={<TimeTrackerPage />} />}
            {canAccessTracker && <Route path="/tracker/:tab/task/:taskId" element={<TimeTrackerPage />} />}
            {canAccessTracker && <Route path="/task/:taskId" element={<TaskPage />} />}
            {canAccessProjects && <Route path="/projects" element={<ProjectsPage />} />}
            {canAccessProjects && <Route path="/projects/tasks" element={<ProjectsPage />} />}
            {canAccessProjects && <Route path="/projects/tasks/:taskId" element={<ProjectsPage />} />}
            {canAccessProjects && <Route path="/projects/project/:projectId" element={<ProjectPage />} />}
            {canAccessTasks && <Route path="/tasks" element={<TasksPage />} />}
            {canAccessInvoices && <Route path="/invoices" element={<InvoicesPage />} />}
            {canAccessInvoices && <Route path="/invoices/:id" element={<InvoiceDetailPage />} />}
            {canAccessProductionManagement && <Route path="/production-management" element={<Navigate to="/production-management/dashboard" replace />} />}
            {canAccessProductionManagement && <Route path="/production-management/dashboard" element={<ProductionManagementDashboardPage />} />}
            {canAccessProductionManagement && <Route path="/production-management/projects" element={<ProductionManagementPage />} />}
            {canAccessProductionManagement && <Route path="/production-management/projects/:id" element={<ProductionManagementDetailPage />} />}
            {canAccessProductionManagement && <Route path="/production-management/task/:taskId" element={<ProductionManagementTaskPage />} />}
            {canAccessProductionManagement && <Route path="/production-management/timesheets" element={<ProductionTimesheetsPage />} />}
            {canAccessProductionManagement && <Route path="/production-management/resources" element={<ResourcesPage />} />}
            {canAccessProductionManagement && <Route path="/production-management/resources" element={<ResourcesPage />} />}
            {canAccessApplications && <Route path="/applications" element={<MyApplicationsPage />} />}
            {canAccessApplications && <Route path="/applications/:id" element={<ApplicationDetailPage />} />}
            {canAccessJobs && <Route path="/jobs" element={<MyJobListingsPage />} />}
            {canAccessJobs && <Route path="/jobs/:id" element={<JobDetailPage />} />}
            {canAccessInfluencerProfile && <Route path="/influencer-profile" element={<InfluencerProfilePage />} />}
            {canAccessInfluencerProfile && <Route path="/influencer-profile/campaigns/:id" element={<CampaignDetailsPage />} />}
            {canAccessSupport && <Route path="/support" element={<SupportPage />} />}
            {canAccessSettings && <Route path="/settings" element={<SettingsPage />} />}
            {/* Figmake design pages - accessible if tracker or production management is enabled */}
            {(canAccessTracker || canAccessProductionManagement) && <Route path="/figmake/dashboard" element={<FigmakeDashboardPage />} />}
            {(canAccessTracker || canAccessProductionManagement) && <Route path="/figmake/timesheet" element={<FigmakeTimesheetPage />} />}
            <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
            <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
          </Routes>
        </main>
      </SidebarInset>
    </SidebarProvider>
    </div>
  )
}

export default function App() {
  // Get feature flags to conditionally enable providers
  const features = (window as any).WP_COCKPIT?.features || {}
  const canAccessProductionManagement = features.production_management === 'yes'
  const canAccessProjects = features.projects === 'yes'
  const canAccessTasks = features.tasks === 'yes'
  const canAccessTracker = features.tracker === 'yes'
  
  // Only enable Sprint provider if tasks/projects/tracker are enabled
  const enableSprintProvider = canAccessProjects || canAccessTasks || canAccessTracker
  
  return (
    <ThemeProvider defaultTheme="light">
      <WordPressAuthProvider>
        {canAccessProductionManagement ? (
          <ProductionManagementProvider>
            {enableSprintProvider ? (
              <SprintProvider>
                <TimerProvider>
                  <ProtectedRoutes />
                </TimerProvider>
              </SprintProvider>
            ) : (
              <TimerProvider>
                <ProtectedRoutes />
              </TimerProvider>
            )}
          </ProductionManagementProvider>
        ) : enableSprintProvider ? (
          <SprintProvider>
            <TimerProvider>
              <ProtectedRoutes />
            </TimerProvider>
          </SprintProvider>
        ) : (
          <TimerProvider>
            <ProtectedRoutes />
          </TimerProvider>
        )}
      </WordPressAuthProvider>
    </ThemeProvider>
  )
}
